/**
 * TemplatesSidebar — the clinic's saved menu templates (ארוחות / אפשרויות / מזונות).
 *
 * CONTROLLED: the menu-builder page owns the selection. Clicking a card calls
 * onSelectTemplate(template); clicking the already-selected card (or ביטול)
 * calls onSelectTemplate(null). The drop zones and the placement scrim belong
 * to the page — this component only picks what is about to be placed.
 *
 * Macro totals come from the stored `totals` when the template has them, and
 * fall back to src/utils/nutritionCalc — the math is never re-implemented here.
 *
 * Error codes: CK-10150 (load templates), CK-10151 (delete a template).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  BookmarkSimple,
  Check,
  ForkKnife,
  ListBullets,
  Plus,
  Trash,
} from "@phosphor-icons/react";

import { MENU_TEMPLATE_KINDS } from "../../../firebase";
import {
  useDeleteMenuTemplateMutation,
  useGetMenuTemplatesQuery,
} from "../../../store/services/menusApi";
import {
  selectTherapistId,
  selectTherapistName,
} from "../../../store/slices/authSlice";
import {
  calcBlockNutrition,
  calcIngredientGroupNutrition,
  calcSubBlockNutrition,
} from "../../../utils/nutritionCalc";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { useToast } from "../../../contexts/ToastContext";
import HoverTip from "../../ui/HoverTip";
import "./TemplatesSidebar.css";
import ContentLoader from "../../shared/ContentLoader";

// ============================================================================
// Constants
// ============================================================================

/** The three sections, in display order. doof shipped only the first two. */
const SECTIONS = [
  {
    kind: MENU_TEMPLATE_KINDS.BLOCK,
    title: "בלוקים",
    icon: ForkKnife,
    empty: "עדיין לא שמרתם בלוקים",
  },
  {
    kind: MENU_TEMPLATE_KINDS.SUB_BLOCK,
    title: "תת-בלוקים",
    icon: ListBullets,
    empty: "עדיין לא שמרתם תת-בלוקים",
  },
  {
    kind: MENU_TEMPLATE_KINDS.FOOD,
    title: "מזונות",
    icon: BookmarkSimple,
    empty: "עדיין לא שמרתם מזונות",
  },
];

/** How many summary lines a card shows before collapsing to "+n נוספים". */
const MAX_SUMMARY_LINES = 3;

const MACRO_FIELDS = [
  { key: "calories", label: "קלוריות" },
  { key: "carbohydrates", label: "פחמימות" },
  { key: "protein", label: "חלבון" },
  { key: "fat", label: "שומן" },
];

// ============================================================================
// Helpers (presentation only)
// ============================================================================

const round = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? Math.round(num) : 0;
};

/** "2 כף של גבינה לבנה" — an ingredient in one Hebrew line. */
const describeIngredient = (ingredient) => {
  if (!ingredient) return "";
  const name = ingredient.foodName || "מזון";
  const amount = Number(ingredient.amount);
  if (!Number.isFinite(amount) || amount <= 0) return name;
  const unit = ingredient.selectedAmount || "";
  return unit ? `${amount} ${unit} של ${name}` : `${amount} ${name}`;
};

/** The summary lines a template card shows under its name. */
const describePayload = (kind, payload) => {
  if (!payload) return [];

  if (kind === MENU_TEMPLATE_KINDS.FOOD) {
    const lines = [describeIngredient(payload)];
    const substitutes = payload.connectedIngredients || [];
    if (substitutes.length > 0) {
      lines.push(`${substitutes.length} חלופות`);
    }
    return lines;
  }

  if (kind === MENU_TEMPLATE_KINDS.SUB_BLOCK) {
    return (payload.ingredients || []).map(describeIngredient);
  }

  return (payload.subBlocks || []).map((subBlock) => {
    const count = (subBlock?.ingredients || []).length;
    const name = subBlock?.subBlockName || "תת-בלוק ללא שם";
    return `${name} · ${count} פריטים`;
  });
};

/**
 * Macro totals for a template: the stored snapshot when it exists, otherwise
 * computed with the shared calculator.
 */
const templateTotals = (template) => {
  const stored = template?.totals;
  if (stored && MACRO_FIELDS.some(({ key }) => stored[key] !== undefined)) {
    return {
      calories: round(stored.calories),
      protein: round(stored.protein),
      carbohydrates: round(stored.carbohydrates),
      fat: round(stored.fat),
    };
  }

  const payload = template?.payload;
  if (!payload) return { calories: 0, protein: 0, carbohydrates: 0, fat: 0 };

  if (template.kind === MENU_TEMPLATE_KINDS.BLOCK) {
    return calcBlockNutrition(payload);
  }
  if (template.kind === MENU_TEMPLATE_KINDS.SUB_BLOCK) {
    return calcSubBlockNutrition(payload);
  }

  const totals = calcIngredientGroupNutrition(payload);
  return {
    calories: round(totals.calories),
    protein: round(totals.protein),
    carbohydrates: round(totals.carbohydrates),
    fat: round(totals.fat),
  };
};

// ============================================================================
// Component
// ============================================================================

/**
 * @param {string} clinicId
 * @param {Object|null} selectedTemplate owned by the page
 * @param {function} onSelectTemplate (template|null)
 */
const TemplatesSidebar = ({ clinicId, selectedTemplate, onSelectTemplate }) => {
  const { showSuccess, showError } = useToast();
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);

  const {
    data: templates,
    isLoading,
    error,
  } = useGetMenuTemplatesQuery({ clinicId }, { skip: !clinicId });

  useEffect(() => {
    if (!error) return;
    console.error("[CK-10150] Error loading menu templates:", {
      errorCode: "CK-10150",
      errorMessage: error?.message || "unknown",
      clinicId,
      timestamp: new Date().toISOString(),
    });
  }, [error, clinicId]);

  const [deleteMenuTemplate, { isLoading: isDeleting }] =
    useDeleteMenuTemplateMutation();
  const [templateToDelete, setTemplateToDelete] = useState(null);

  /** Split by kind in memory — the query returns one flat list. */
  const byKind = useMemo(() => {
    const groups = SECTIONS.reduce((acc, section) => {
      acc[section.kind] = [];
      return acc;
    }, {});
    (templates || []).forEach((template) => {
      if (groups[template?.kind]) groups[template.kind].push(template);
    });
    return groups;
  }, [templates]);

  const handleToggle = useCallback(
    (template) => {
      if (selectedTemplate?.id === template.id) {
        onSelectTemplate(null);
        return;
      }
      onSelectTemplate(template);
    },
    [selectedTemplate, onSelectTemplate]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!templateToDelete) return;
    const templateId = templateToDelete.id;
    try {
      await deleteMenuTemplate({
        clinicId,
        templateId,
        name: templateToDelete.name,
        therapistId,
        therapistName,
      }).unwrap();

      if (selectedTemplate?.id === templateId) onSelectTemplate(null);
      showSuccess("התבנית נמחקה");
      setTemplateToDelete(null);
    } catch (err) {
      console.error("[CK-10151] Error deleting a menu template:", {
        errorCode: "CK-10151",
        errorMessage: err?.message || err?.data?.message || "unknown",
        clinicId,
        templateId,
        timestamp: new Date().toISOString(),
      });
      showError("מחיקת התבנית נכשלה, נסו שוב");
    }
  }, [
    templateToDelete,
    clinicId,
    therapistId,
    therapistName,
    deleteMenuTemplate,
    selectedTemplate,
    onSelectTemplate,
    showSuccess,
    showError,
  ]);

  return (
    <div className="ntsb-panel">
      <div className="ntsb-head">
        <span className="ntsb-head-icon">
          <BookmarkSimple size={20} weight="fill" />
        </span>
        <HoverTip
          text="בלוקים, תת-בלוקים ומזונות ששמרתם מתפריטים קודמים (סמל הסימנייה). לוחצים על תבנית, ואז על ״הוסף כאן״ במקום הרצוי בתפריט"
          placement="bottom"
        >
          <div className="ntsb-head-text" tabIndex={0}>
            <span className="ntsb-eyebrow">תבניות המרפאה</span>
            <h3>שמורים לשימוש חוזר</h3>
          </div>
        </HoverTip>
      </div>

      <div className="ntsb-scroll">
        {isLoading ? (
          <ContentLoader size="sm" compact message="טוען תבניות..." />
        ) : error ? (
          <p className="ntsb-error">טעינת התבניות נכשלה</p>
        ) : (
          SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            const items = byKind[section.kind] || [];
            return (
              <section className="ntsb-section" key={section.kind}>
                <div className="ntsb-section-head">
                  <SectionIcon size={14} weight="fill" />
                  <span className="ntsb-section-title">{section.title}</span>
                  <span className="ntsb-section-count">{items.length}</span>
                </div>

                {items.length === 0 ? (
                  <div className="ntsb-empty">
                    <p>{section.empty}</p>
                    <p className="ntsb-empty-hint">
                      לחיצה על סמל הסימנייה{" "}
                      <BookmarkSimple size={11} weight="fill" /> בתפריט שומרת
                      אותו כתבנית לשימוש חוזר
                    </p>
                  </div>
                ) : (
                  <div className="ntsb-cards">
                    {items.map((template) => {
                      const isSelected = selectedTemplate?.id === template.id;
                      const lines = describePayload(
                        template.kind,
                        template.payload
                      );
                      const visibleLines = lines.slice(0, MAX_SUMMARY_LINES);
                      const hiddenCount = lines.length - visibleLines.length;
                      const totals = templateTotals(template);

                      return (
                        <article
                          className={`ntsb-card ${isSelected ? "selected" : ""}`}
                          key={template.id}
                        >
                          <div className="ntsb-card-head">
                            <span className="ntsb-card-name">
                              {template.name}
                            </span>
                            {isSelected && (
                              <span className="ntsb-badge ntsb-badge-selected">
                                <Check size={10} weight="bold" />
                                נבחר להוספה
                              </span>
                            )}
                          </div>

                          {visibleLines.length > 0 && (
                            <ul className="ntsb-card-lines">
                              {visibleLines.map((line, index) => (
                                <li key={`${template.id}-line-${index}`}>
                                  {line}
                                </li>
                              ))}
                              {hiddenCount > 0 && (
                                <li className="ntsb-card-more">
                                  ועוד {hiddenCount} פריטים
                                </li>
                              )}
                            </ul>
                          )}

                          <div className="ntsb-macros">
                            {MACRO_FIELDS.map(({ key, label }) => (
                              <span className="ntsb-macro" key={key}>
                                <span className="ntsb-macro-label">{label}</span>
                                <span className="ntsb-macro-value">
                                  {totals[key]}
                                </span>
                              </span>
                            ))}
                          </div>

                          <div className="ntsb-card-actions">
                            <button
                              type="button"
                              className={`ntsb-add-btn ${
                                isSelected ? "active" : ""
                              }`}
                              onClick={() => handleToggle(template)}
                              aria-pressed={isSelected}
                            >
                              {isSelected ? (
                                <>
                                  <Check size={13} weight="bold" />
                                  <span>ביטול הבחירה</span>
                                </>
                              ) : (
                                <>
                                  <Plus size={13} weight="bold" />
                                  <span>הוסף לתפריט</span>
                                </>
                              )}
                            </button>
                            <HoverTip text="מחיקת התבנית">
                              <button
                                type="button"
                                className="ntsb-icon-btn danger"
                                onClick={() => setTemplateToDelete(template)}
                                aria-label={`מחיקת התבנית ${template.name}`}
                              >
                                <Trash size={14} weight="fill" />
                              </button>
                            </HoverTip>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })
        )}
      </div>

      <ConfirmDialog
        isOpen={!!templateToDelete}
        onClose={() => setTemplateToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="מחיקת תבנית"
        message={`האם למחוק את התבנית "${templateToDelete?.name || ""}"? התפריטים שכבר נבנו לא יושפעו.`}
        confirmText="מחיקה"
        cancelText="ביטול"
        type="danger"
        icon="delete"
        confirmDisabled={isDeleting}
      />
    </div>
  );
};

export default TemplatesSidebar;
